import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleUpsellVisibility);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleUpsellVisibility);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  /**
   * Re-applies a scroll position several times, because the browser can restore
   * its own (wrong) scroll position right after a history traversal.
   * @param {number} top
   */
  #forceScroll(top) {
    const apply = () => window.scrollTo({ top, behavior: 'instant' });
    requestAnimationFrame(apply);
    setTimeout(apply, 60);
    setTimeout(apply, 200);
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();

    if (history.state?.cartDrawerOpen) {
      // The dialog has already restored the scroll position at this point.
      // history.back() would make the browser restore the position recorded
      // while the body was scroll-locked (top of the page), so we keep it.
      const restoreTo = window.scrollY;
      const previousRestoration = 'scrollRestoration' in history ? history.scrollRestoration : null;

      if (previousRestoration !== null) history.scrollRestoration = 'manual';

      history.back();
      this.#forceScroll(restoreTo);

      if (previousRestoration !== null) {
        setTimeout(() => {
          history.scrollRestoration = previousRestoration;
        }, 400);
      }
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      const restoreTo = this.#lockedScrollY();

      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');

      if (restoreTo != null) this.#forceScroll(restoreTo);
    }
  };

  /**
   * Reads the scroll position stored by the dialog scroll lock (body top: -Ypx).
   * @returns {number | null}
   */
  #lockedScrollY() {
    const top = document.body.style.top;
    if (!top) return null;
    const value = parseInt(top, 10);
    return Number.isNaN(value) ? null : Math.abs(value);
  }

  #handleCartAdd = async () => {
    await this.#refreshCartDrawer();
    await this.#handleUpsellVisibility();

    this.showDialog();
  };

  async #refreshCartDrawer() {
    try {
      const response = await fetch('/?sections=cart-drawer');
      const data = await response.json();

      if (!data['cart-drawer']) return;

      const parsed = new DOMParser().parseFromString(data['cart-drawer'], 'text/html');
      const newDrawer = parsed.querySelector('cart-drawer-component');

      if (!newDrawer) return;

      const currentDialog = this.refs.dialog;
      const newDialog = newDrawer.querySelector('dialog');

      if (currentDialog && newDialog) {
        // Replace only the contents of the dialog so the <dialog> element itself
        // (and its open state, and the scroll position it has stored) survives.
        currentDialog.innerHTML = newDialog.innerHTML;
      } else {
        this.innerHTML = newDrawer.innerHTML;
      }

      this.#updateStickyState();
    } catch (error) {
      console.error('Error refrescando cart drawer:', error);
    }
  }

  #handleUpsellVisibility = async () => {
    try {
      const upsells = this.querySelectorAll('[data-cart-upsell]');
      if (!upsells.length) return;

      const response = await fetch('/cart.js');
      const cart = await response.json();

      upsells.forEach((upsell) => {
        const productId = Number(upsell.dataset.productId);
        const yaEnCarrito = cart.items.some((item) => item.product_id === productId);
        upsell.style.display = yaEnCarrito ? 'none' : 'block';
      });
    } catch (error) {
      console.error('Error actualizando upsell del carrito:', error);
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState = () => {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  };
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
